import { CurrencyPipe } from "@angular/common";
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from "@angular/core";
import { AbstractControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatDivider } from "@angular/material/divider";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ForgeCardModule } from "@tylertech/forge-angular";
import { LgdFormControlFactory, LgdRadioGroupComponent } from "@tylertech/lgd-reactive-forms";
import { AccountDetailDrilldownViewModel, AccountDetailViewModel } from "@tylertech/tyler-api-types";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AccountDetailDialogComponent, AccountDetailDialogData, AccountDetailType } from "./accountDetailDialog.component";
import { AccountDetailPieChartComponent } from "./accountDetailPieChart.component";

// TODO what do these mean? and what uses this?
export type EntityType = "P" | "G" | "PA";

const enum AccountType
{
    Revenue = "Revenue",
    Expense = "Expense",
    Total = "Total",
}

@Component({
    selector: "account-detail-card",
    templateUrl: "./accountDetailCard.tmpl.html",
    styleUrls: [
        "./accountDetailCard.component.scss"
    ],
    imports: [
        AccountDetailPieChartComponent,
        CurrencyPipe,
        FlexDirective,
        ForgeCardModule,
        LayoutAlignDirective,
        LayoutDirective,
        LgdRadioGroupComponent,
        MatDivider,
        ReactiveFormsModule
    ]
})
export class AccountDetailCardComponent implements OnInit, OnChanges, OnDestroy
{
    private readonly matDialog = inject(MatDialog);

    private readonly destructor$ = new Subject<void>();
    private readonly accountTypesSubject = new BehaviorSubject<Array<AccountType>>(
        [AccountType.Revenue, AccountType.Expense, AccountType.Total]);

    chartData: AccountDetailViewModel = undefined;

    readonly formGroup: FormGroup;

    @Input() account: AccountDetailViewModel;

    @Input("budgetAvailableLabel") _budgetAvailableLabel: string;
    @Input("budgetRemainingLabel") _budgetRemainingLabel: string;
    @Input("currentBudgetLabel") _currentBudgetLabel: string;
    @Input("pendingLabel") _pendingLabel: string;

    @Input() chartHasData: boolean;

    /** @deprecated */
    @Input() type: EntityType;
    @Input() link: string;

    @Input("totals") private readonly _totals: ReadonlyArray<AccountDetailViewModel>;
    isLoaded: boolean;

    constructor()
    {
        this.formGroup = new FormGroup({
            accountType: LgdFormControlFactory.radioGroup<AccountType>(
                AccountType.Revenue,
                false),
        });
    }

    private get accountTypeControl(): AbstractControl<AccountType> { return this.formGroup.get("accountType"); }
    private get accountType(): AccountType { return this.accountTypeControl.value; }
    private get totals(): ReadonlyArray<AccountDetailViewModel> { return this._totals || [this.account]; }

    get accountTypes(): Observable<Array<AccountType>> { return this.accountTypesSubject.asObservable(); }
    // get chartData(): AccountDetailViewModel { return this.chartDataSubject.asObservable(); }
    // TODO The only time this is false is if we aren't given a value at all....
    get showLink(): boolean { return this.type && (this.type === "P" || this.type === "G"); }
    // get chartAccount(): AccountDetailViewModel { return this.chartDataSubject.value; }
    // get hasAccount(): boolean { return !!this.chartAccount; }
    get hasSeparateTotals(): boolean { return this.totals?.length === 3; }

    get budgetAvailableLabel(): string { return this._budgetAvailableLabel || "Budget Available"; }
    get budgetRemainingLabel(): string { return this._budgetRemainingLabel || "Budget Remaining"; }
    get currentBudgetLabel(): string { return this._currentBudgetLabel || "Current Budget"; }
    get pendingLabel(): string { return this._pendingLabel || "Pending"; }

    get CurrentBudget(): number { return this.chartData.CurrentBudget; }
    get Activity(): number { return this.chartData.Activity; }
    get Encumbrance(): number { return this.chartData.Encumbrance; }
    get Reserves(): number { return this.chartData.Reserves; }
    get Pending(): number { return this.chartData.Pending; }
    get BeginningBalance(): number { return this.chartData.BeginningBalance; }
    get BudgetRemaining(): number { return this.chartData.BudgetRemaining; }
    get BudgetOriginal(): number { return this.chartData.BudgetOriginal; }
    get BudgetAdjustment(): number { return this.chartData.BudgetAdjustment; }
    get BudgetAvailable(): number { return this.chartData.BudgetAvailable; }
    get EndingBalance(): number { return this.chartData.EndingBalance; }
    get PriorYearAdjustment(): number { return this.chartData.PriorYearAdjustment; }

    ngOnDestroy(): void
    {
        this.accountTypesSubject.complete();

        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        if (changes.totals && !changes.totals.firstChange)
        {
            const currentTotals = changes.totals.currentValue as ReadonlyArray<AccountDetailViewModel>;
            if (currentTotals?.length > 0)
            {
                this.chartData = currentTotals[this.accountType];
            }
        }
    }

    ngOnInit(): void
    {
        const self = this;

        this.chartData = this.totals ? this.totals[0] : this.account;

        subscribeToValueChanges(this.accountTypeControl, (newValue: AccountType) =>
        {
            if (this.totals)
            {
                // Create a new object reference to trigger change detection
                this.chartData = { ...this.totals[newValue] };
            }
            else if (this.account)
            {
                this.chartData = { ...this.account };
            }
        });

        this.isLoaded = true;

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    drillDownOnActivity($event: MouseEvent): void
    {
        $event?.stopPropagation();

        this.openDetailDialog("Activity");
    }

    drillDownOnEncumbrances($event: MouseEvent): void
    {
        $event?.stopPropagation();

        this.openDetailDialog("Encumbrance");
    }

    drillDownOnReserves($event: MouseEvent): void
    {
        $event?.stopPropagation();

        this.openDetailDialog("Reserve");
    }

    private openDetailDialog(type: AccountDetailType): void
    {
        const fetch: AccountDetailDrilldownViewModel = {
            Id: this.totals[this.accountType].AccountId,
            AccountType: this.type,
            IdType: "Account",
            FiscalId: this.totals[this.accountType].FiscalId,
            PeriodId: this.totals[this.accountType].PeriodId,
        };

        const dialogConfig: MatDialogConfig<AccountDetailDialogData> = {
            data: {
                fetch,
                type
            }
        };
        this.matDialog.open(AccountDetailDialogComponent, dialogConfig);
    }
}
